import { Button } from '@auctisync/ui';
import { useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';

type ForgotPasswordFormProps = {
  /**
   * Absolute URL Supabase should redirect the reset link to. Must be
   * allowlisted in Supabase Auth → URL Configuration → Redirect URLs.
   */
  resetRedirectTo: string;
};

type Mode = 'idle' | 'submitting' | 'sent';

/**
 * Collects an email address and triggers a Supabase password-reset email.
 * The recipient clicks the email link → lands at `resetRedirectTo` with a
 * `PASSWORD_RECOVERY` event, handled by AuthCallback.
 */
export function ForgotPasswordForm({ resetRedirectTo }: ForgotPasswordFormProps) {
  const [email, setEmail] = useState('');
  const [mode, setMode] = useState<Mode>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMode('submitting');

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: resetRedirectTo,
    });

    if (resetError) {
      setError(resetError.message);
      setMode('idle');
      return;
    }

    setMode('sent');
  }

  if (mode === 'sent') {
    return (
      <div
        role="status"
        style={{
          padding: '1rem',
          borderRadius: '0.5rem',
          background: 'var(--surface-muted, #f5f5f5)',
          color: 'var(--text, #111)',
          fontSize: '0.875rem',
          lineHeight: 1.5,
        }}
      >
        <strong>Check your email.</strong> If an account exists for <code>{email.trim()}</code>, we
        sent a reset link. The link expires in 1 hour.
        <br />
        <br />
        Didn&rsquo;t get it within a few minutes? Check your spam folder, and make sure you entered
        the same email you signed up with.
      </div>
    );
  }

  const busy = mode === 'submitting';
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: 'var(--radius-sm, 0.375rem)',
    border: '1px solid var(--border, #e5e7eb)',
    background: 'var(--surface, #ffffff)',
    color: 'var(--text, #111)',
    fontFamily: 'var(--font-body, system-ui)',
    fontSize: '0.875rem',
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-muted, #6b7280)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(e);
      }}
      style={{ display: 'grid', gap: '0.75rem' }}
    >
      <div>
        <label htmlFor="forgot-email" style={labelStyle}>
          Email
        </label>
        <input
          id="forgot-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          disabled={busy}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
      </div>

      {error && (
        <div
          role="alert"
          style={{
            fontSize: '0.875rem',
            color: 'var(--semantic-error, #b91c1c)',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-sm, 0.375rem)',
            background: 'var(--semantic-error-bg, #fef2f2)',
            border: '1px solid var(--semantic-error, #b91c1c)',
          }}
        >
          {error}
        </div>
      )}

      <Button type="submit" disabled={busy}>
        {busy ? 'Sending…' : 'Send reset link'}
      </Button>
    </form>
  );
}
